import React, { ReactNode } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import type { DialogProps } from "@mui/material/Dialog";
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const isMessageDescriptor = (value: any): value is MessageDescriptor => {
    return value && typeof value === "object" && "id" in value && "defaultMessage" in value;
};

type TitledDialogProps = {
    open: boolean;
    isContentChanged: () => boolean;
    hide: () => void;
    onExited?: () => void;
    title: MessageDescriptor | React.ReactNode;
    children: ReactNode;
    actions?: ReactNode;
    /** Optional semantic/test label, applied as data-dialog on the root Dialog */
    dialogLabel?: string;
} & Omit<DialogProps, "open" | "onClose" | "title">;

const TitledDialog: React.FC<TitledDialogProps> = ({
    open,
    isContentChanged,
    hide,
    onExited,
    title,
    children,
    actions,
    dialogLabel,
    ...rest
}) => {
    const { formatMessage } = useIntl();
    const titleIsMessageDescriptor = isMessageDescriptor(title);

    const handleClose = () => {
        if (!isContentChanged() || confirm(formatMessage({ id: "confirm_dialog_close", defaultMessage: "You have unsaved changes. Are you sure you wish to close the dialog?" }))) hide();
    };

    // Build IDs and labels for accessibility
    const titleId = titleIsMessageDescriptor ? `${title.id}-title` : undefined;
    const dataDialog = dialogLabel ?? (titleIsMessageDescriptor ? title.id : undefined);
    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            onClose={handleClose}
            onTransitionExited={onExited}
            onClick={(event) => event.stopPropagation()}
            aria-labelledby={titleId}
            data-dialog={dataDialog}
            {...rest}
        >
            <DialogTitle id={titleId}>
                {titleIsMessageDescriptor ? <FormattedMessage {...title} /> : title}
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                    }}
                >
                    <FontAwesomeIcon icon={faClose} />
                </IconButton>
            </DialogTitle>
            <DialogContent>{children}</DialogContent>
            {actions && <DialogActions>{actions}</DialogActions>}
        </Dialog>
    );
};

export default TitledDialog;
